/*
  Synchronisation du brouillon local avec le serveur.

  Deux parties :
   · planSync(brouillon, etatServeur) — PUR, sans réseau : décide QUOI faire.
     C'est la pièce la plus délicate du mode hors ligne, donc elle est isolée et
     testée pour tous les scénarios (coupure, double synchro, feuille déjà
     validée, première ouverture).
   · runSync(...) — exécute le plan via l'API.

  STRATÉGIE « le brouillon local fait autorité tant que la feuille est un
  brouillon serveur ». La synchronisation d'un brouillon non vide REMPLACE les
  lignes serveur par les lignes locales (suppression puis recréation dans
  l'ordre). C'est ce qui rend la synchro IDEMPOTENTE : la rejouer deux fois donne
  le même résultat, une reprise après coupure ne duplique rien. Le serveur
  rechaîne les départs à la recréation, exactement comme en ligne.

  Une feuille déjà SOUMISE ou VALIDÉE côté serveur n'est plus un brouillon : on
  ne la remplace pas — on ADOPTE l'état serveur et on abandonne le brouillon
  local. Aucun argent n'est en jeu (le serveur est la source de vérité une fois
  soumis), et le déclarant voit l'état réel.
*/

const STATUTS_FIGES = ['SOUMISE', 'VALIDEE'];

// Traduit une ligne de brouillon local en corps de requête POST /mine/ligne.
// On n'envoie PAS le départ : le serveur le rechaîne depuis l'arrivée
// précédente, comme en ligne — une seule source de vérité pour l'enchaînement.
export function corpsCreation(ligne, date) {
  const corps = { date, type: ligne.type, montant_fc: Number(ligne.montant_fc) || 0 };
  if (ligne.id_point_arrivee) corps.id_point_arrivee = ligne.id_point_arrivee;
  else if (ligne.libelle_arrivee) corps.libelle_arrivee = ligne.libelle_arrivee;
  if (ligne.id_moyen) corps.id_moyen = ligne.id_moyen;
  if (ligne.type === 'LIVRAISON' && ligne.id_commande) corps.id_commande = ligne.id_commande;
  // Motif d'un montant au-dessus de l'habituel : le serveur accepte `note`
  // depuis toujours, le front ne l'envoyait simplement jamais.
  if (ligne.note) corps.note = ligne.note;
  return corps;
}

/**
 * @param {object} brouillon  { lignes:[...], statut:'BROUILLON'|'A_SOUMETTRE' }
 * @param {object} serveur    { feuille:{statut,...}|null, lignes:[...] }  (état renvoyé par GET /mine/jour)
 * @returns {object} plan :
 *   { action:'adopter' , message? }                       → prendre l'état serveur, jeter le local
 *   { action:'pousser' , ops:[...], soumettre:bool }      → remplacer le serveur par le local
 *   { action:'rien' }                                     → déjà aligné, rien à faire
 */
export function planSync(brouillon, serveur) {
  const statutServeur = serveur && serveur.feuille ? String(serveur.feuille.statut || 'BROUILLON') : null;
  const lignesServeur = (serveur && serveur.lignes) || [];
  const lignesLocales = (brouillon && brouillon.lignes) || [];
  const veutSoumettre = brouillon && brouillon.statut === 'A_SOUMETTRE';

  // 1) Feuille déjà soumise/validée côté serveur : elle nous échappe. On adopte.
  if (statutServeur && STATUTS_FIGES.includes(statutServeur)) {
    return {
      action: 'adopter',
      message: statutServeur === 'VALIDEE'
        ? 'Cette feuille a déjà été validée par le bureau.'
        : 'Cette feuille a déjà été soumise.',
    };
  }

  // 2) Rien en local : on prend simplement ce que le serveur a (première
  //    ouverture, ou brouillon local effacé). Rien à pousser.
  if (!lignesLocales.length) {
    return { action: 'adopter' };
  }

  // 3) Le brouillon local fait autorité : on remplace les lignes serveur par les
  //    nôtres. Supprimer d'abord (idempotent : une ligne déjà absente est
  //    tolérée à l'exécution), puis recréer dans l'ordre.
  const ops = [];
  for (const l of lignesServeur) {
    if (l && l.id_ligne) ops.push({ op: 'supprimer', id_ligne: l.id_ligne });
  }
  // Les segments d'un même PARCOURS partent ensemble, en une seule opération :
  // le serveur les écrit dans UNE transaction (/mine/parcours) et les relie par
  // `id_parcours`. Les envoyer un par un via /mine/ligne perdrait ce lien, donc
  // le regroupement pliable à l'affichage et le total du parcours.
  //
  // On regroupe les lignes CONSÉCUTIVES, comme le fait l'affichage : deux
  // parcours distincts ne peuvent pas se mélanger, et une saisie intercalée
  // entre deux segments coupe naturellement le groupe.
  let i = 0;
  while (i < lignesLocales.length) {
    const l = lignesLocales[i];
    if (!l.id_parcours) { ops.push({ op: 'creer', ligne: l }); i += 1; continue; }
    const groupe = [l];
    let j = i + 1;
    while (j < lignesLocales.length && lignesLocales[j].id_parcours === l.id_parcours) {
      groupe.push(lignesLocales[j]); j += 1;
    }
    ops.push({ op: 'creer_parcours', id_parcours: l.id_parcours, id_commande: l.id_commande || null, lignes: groupe });
    i = j;
  }
  return { action: 'pousser', ops, soumettre: Boolean(veutSoumettre) };
}

// --- Exécution --------------------------------------------------------------
// requester : instance axios (userRequest). date : jour de la feuille.
// Renvoie { ok, adopte?, message?, erreur? }. Ne LÈVE pas : le résultat est
// destiné à piloter l'affichage (bannière de synchro), pas à casser l'écran.
export async function runSync({ requester, date, brouillon }) {
  let serveur;
  try {
    const { data } = await requester.get('/api/feuille-depense/mine/jour', { params: date ? { date } : {} });
    serveur = { feuille: data.feuille, lignes: data.lignes || [] };
  } catch (e) {
    // Pas de réseau (ou erreur serveur) : on reste hors ligne, sans rien casser.
    return { ok: false, horsLigne: true, erreur: reseauMessage(e) };
  }

  const plan = planSync(brouillon, serveur);

  if (plan.action === 'adopter' || plan.action === 'rien') {
    return { ok: true, adopte: true, message: plan.message || null };
  }

  // plan.action === 'pousser'
  try {
    for (const op of plan.ops) {
      if (op.op === 'supprimer') {
        await supprimerTolerant(requester, op.id_ligne);
      } else if (op.op === 'creer') {
        await requester.post('/api/feuille-depense/mine/ligne', corpsCreation(op.ligne, date));
      } else if (op.op === 'creer_parcours') {
        // Une transaction serveur pour tout le parcours. L'endpoint est
        // IDEMPOTENT sur (id_feuille, id_parcours) : un rejeu après coupure
        // renvoie `rejeu: true` sans rien dupliquer. Et comme le plan supprime
        // d'abord les lignes serveur, l'idempotence — qui s'appuie justement sur
        // la présence de ces lignes — ne bloque pas la recréation. Vérifié :
        // c'est ce qui rend « supprimer puis rejouer » sûr ici.
        await requester.post('/api/feuille-depense/mine/parcours', {
          date,
          id_commande: op.id_commande,
          id_parcours: op.id_parcours,
          segments: op.lignes.map((l) => ({
            id_point_depart: l.id_point_depart,
            id_point_arrivee: l.id_point_arrivee,
            montant_fc: Number(l.montant_fc) || 0,
            // UNE COMMANDE PAR SEGMENT : une tournée dessert plusieurs clientes.
            // Sans cette ligne, le serveur retombait sur `op.id_commande` (celle
            // du PREMIER segment) et le coût du 2ᵉ arrêt tombait sur la 1ʳᵉ.
            id_commande: l.id_commande || null,
            // Trace d'un montant global négocié pour toute la course.
            note: l.note || null,
          })),
        });
      }
    }
    if (plan.soumettre) {
      await requester.post('/api/feuille-depense/mine/soumettre', date ? { date } : {});
    }
    return { ok: true, adopte: false, soumis: Boolean(plan.soumettre) };
  } catch (e) {
    // Une écriture a échoué en cours de route. Si la feuille a basculé
    // SOUMISE/VALIDÉE entre-temps (course avec le bureau), le message serveur le
    // dira ; l'appelant recharge alors l'état réel. Sinon, c'est le réseau qui a
    // lâché : le brouillon local reste intact, la synchro se rejouera.
    const status = e && e.response && e.response.status;
    if (status === 409) {
      return { ok: false, conflit: true, erreur: (e.response.data && e.response.data.error) || 'Feuille déjà traitée.' };
    }
    return { ok: false, horsLigne: !status, erreur: reseauMessage(e) };
  }
}

async function supprimerTolerant(requester, idLigne) {
  try {
    await requester.delete(`/api/feuille-depense/mine/ligne/${idLigne}`);
  } catch (e) {
    // 404 : la ligne n'existe déjà plus (reprise après coupure) → toléré.
    const status = e && e.response && e.response.status;
    if (status !== 404) throw e;
  }
}

function reseauMessage(e) {
  if (e && e.response && e.response.data && e.response.data.error) return e.response.data.error;
  return 'Réseau indisponible.';
}
